import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { ChangeRequest, SellerChangeRequestStatus, BatchedChanges, Project } from '@/types';
import { useAsyncState, useParamReplace } from '@/utils/hooks';
import { searchChangeRequests } from '@/services/change-requests';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import ChangeRequestList from '../change-requests/ChangeRequestList/ChangeRequestList';
import ResidentialUnitGrouping from '../residential-units/ResidentialUnitGrouping/ResidentialUnitGrouping';
import ResidentialUnitGroupingHeader from '../residential-units/ResidentialUnitGrouping/ResidentialUnitGroupingHeader';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { ButtonBar } from '@/components/Buttons';
import { Tabs } from '@ygtechnologies/rubus-ui';
import { Link } from 'react-router-dom';
import { Trans } from '@lingui/react/macro';
import { useParams, Redirect } from 'react-router';

export enum IncompleteRequestMode {
    Unassigned = 'unassigned',
    NoOffer = 'no-offer',
    ReadyToSend = 'ready-to-send',
    Rejected = 'rejected',
    Canceled = 'canceled',
}

interface ProjectChangeRequestsIncompleteProps {
    // mode: IncompleteRequestMode;
}

type ModesMap = {
    [key in IncompleteRequestMode]: {
        filter(data: ChangeRequest): boolean;
        visible(project: Project): boolean;
    };
};
const modes: ModesMap = {
    [IncompleteRequestMode.Unassigned]: {
        filter: (x) => !x.hasParticipants,
        visible: (project) => project.permissions.canAssignChangeRequests,
    },
    [IncompleteRequestMode.NoOffer]: {
        filter: (x) => x.waitingForCurrentUser && x.status === SellerChangeRequestStatus.AwaitingOffers,
        visible: (project) => true,
    },
    [IncompleteRequestMode.ReadyToSend]: {
        filter: (x) => x.status === SellerChangeRequestStatus.ReadyToSend,
        visible: (project) => project.permissions.canSendChangeRequests,
    },
    [IncompleteRequestMode.Rejected]: {
        filter: (x) => x.status === SellerChangeRequestStatus.Rejected,
        visible: (project) => project.permissions.canSendChangeRequests,
    },
    [IncompleteRequestMode.Canceled]: {
        filter: (x) => x.status === SellerChangeRequestStatus.CanceledBySeller,
        visible: (project) => project.permissions.canSendChangeRequests,
    },
};

const ProjectChangeRequestsIncomplete: React.FC<ProjectChangeRequestsIncompleteProps> = () => {
    const project = useSelector((state: IState) => state.global.activeProject!);
    const projectId = project.id;

    const [changeRequests, setChangeRequests, reloadChangeRequests] = useAsyncState(
        () =>
            searchChangeRequests({
                projectId,
                statuses: [
                    SellerChangeRequestStatus.AwaitingOffers,
                    SellerChangeRequestStatus.Unassigned,
                    SellerChangeRequestStatus.ReadyToSend,
                    SellerChangeRequestStatus.Rejected,
                    SellerChangeRequestStatus.CanceledBySeller,
                ],
            }),
        [projectId],
        null
    );

    const [changeRequestTabMap, setChangeRequestTabMap] = useState(new Map<number, IncompleteRequestMode[]>());
    useEffect(() => {
        if (!changeRequests) {
            return;
        }

        setChangeRequestTabMap((oldValue) => {
            var newValue = new Map(oldValue);

            const modesArray = (Object.keys(modes) as IncompleteRequestMode[]).map((mode) => ({
                ...modes[mode],
                mode,
            }));

            changeRequests!.forEach((cr) => {
                if (!newValue.has(cr.id)) {
                    newValue.set(
                        cr.id,
                        modesArray.filter((tab) => tab.filter(cr)).map((tab) => tab.mode)
                    );
                }
            });

            return newValue;
        });
    }, [changeRequests]);

    const countItems = useCallback(
        (mode: IncompleteRequestMode) => {
            return Array.from(changeRequestTabMap.entries()).filter(([id, m]) => m.includes(mode)).length;
        },
        [changeRequestTabMap]
    );

    const modeButtons = useMemo(
        () => [
            {
                mode: IncompleteRequestMode.Unassigned,
                text: <Trans id="Unassigned_change_request">Unassigned</Trans>,
                count: countItems(IncompleteRequestMode.Unassigned),
            },
            {
                mode: IncompleteRequestMode.NoOffer,
                text: <Trans>Awaiting offers</Trans>,
                count: countItems(IncompleteRequestMode.NoOffer),
            },
            {
                mode: IncompleteRequestMode.ReadyToSend,
                text: <Trans>Ready to send</Trans>,
                count: countItems(IncompleteRequestMode.ReadyToSend),
            },
            {
                mode: IncompleteRequestMode.Rejected,
                text: <Trans>Rejected</Trans>,
                count: countItems(IncompleteRequestMode.Rejected),
            },
            {
                mode: IncompleteRequestMode.Canceled,
                text: <Trans>Canceled</Trans>,
                count: countItems(IncompleteRequestMode.Canceled),
            },
        ],
        [countItems]
    );

    const { mode } = useParams<{ mode: IncompleteRequestMode }>();
    const replaceParams = useParamReplace<{ mode: IncompleteRequestMode }>();

    // const [filteredData, setFilteredData] = useState<ChangeRequest[] | null>(null);
    // useEffect(() => {
    //     const modeOptions = modes[mode];

    //     if (changeRequests) {
    //         setFilteredData(changeRequests.filter(modeOptions.filter));
    //     } else {
    //         setFilteredData(null);
    //     }
    // }, [mode, changeRequests]);

    const handleChange = useCallback(
        (changes: BatchedChanges<ChangeRequest>) => {
            // if (!changeRequests) {
            //     return;
            // }

            setChangeRequests((oldValue) => {
                if (!oldValue) {
                    return oldValue;
                }

                return changes.updateArray(oldValue);
            });
        },
        [setChangeRequests]
    );

    const handleSplit = useCallback(async () => {
        await reloadChangeRequests();
        // setTabDataInvalid(true);
    }, [reloadChangeRequests]);

    const allowedModes = modeButtons.filter((x) => modes[x.mode].visible(project));
    if (!allowedModes.find((x) => x.mode === mode)) {
        return (
            <Redirect
                to={replaceParams({
                    mode: allowedModes[0].mode,
                })}
            />
        );
    }

    if (!changeRequests || !changeRequestTabMap) {
        return <LoadingIndicator />;
    }

    const filteredData = changeRequests.filter((x) => changeRequestTabMap.get(x.id)?.includes(mode));

    return (
        <>
            <ButtonBar>
                <Tabs activeId={mode} variant="pill" size="sm">
                    {allowedModes.map((x) => (
                        <Tabs.Tab
                            key={x.mode}
                            id={x.mode}
                            as={Link}
                            to={replaceParams({ mode: x.mode })}
                            label={x.text}
                            badge={x.count}
                        />
                    ))}
                </Tabs>
            </ButtonBar>

            <ResidentialUnitGrouping
                projectId={projectId}
                items={filteredData}
                emptyText={<ChangeRequestList onChange={() => null} changeRequests={[]} reloadChangeRequests={() => {}} />}>
                {(items, residentialUnit, residentialUnitGroup) => (
                    <React.Fragment key={residentialUnit.id}>
                        <ResidentialUnitGroupingHeader
                            residentialUnit={residentialUnit}
                            residentialUnitGroup={residentialUnitGroup}
                            detailsLinkTab="changes"
                        />
                        <ChangeRequestList changeRequests={items} reloadChangeRequests={handleSplit} onChange={handleChange} />
                    </React.Fragment>
                )}
            </ResidentialUnitGrouping>
        </>
    );
};

export default ProjectChangeRequestsIncomplete;
